import React, { useState, useEffect, useRef } from 'react';
import { getPlayerAvatar } from '../../utils/player';
import { RotateCcw, Watch, Settings, LogOut, Users, Trophy, BarChart2 } from 'lucide-react';
import PairWatchModal from './PairWatchModal';
import ModalCloseButton from '../Modals/ModalCloseButton';
import AnimatedScore from './AnimatedScore';
import { isNativeApp } from '../../utils/platform';

// Custom SVG Padel Racket Icon for Server Badge
function PadelRacketIcon({ className = "w-3.5 h-3.5 text-slate-950" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <circle cx="10" cy="9" r="6" stroke="currentColor" strokeWidth="2" fill="none" />
      <circle cx="9" cy="8" r="0.8" />
      <circle cx="11" cy="8" r="0.8" />
      <circle cx="10" cy="10" r="0.8" />
      <line x1="14.5" y1="13.5" x2="20" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export default function PadelScoreBoard({
  mode = 'classic', // 'classic' | 'mvp'
  team1Players = [],
  team2Players = [],
  servingPlayer = null, // player name
  team1ScoreDisplay = '0',
  team2ScoreDisplay = '0',
  sets = [0, 0],
  games = [0, 0],
  setScores = [],
  isTieBreak = false,
  allPlayers = [],
  onPointScore, // (team: 1 | 2) => void
  onUndo,
  onOpenSettings,
  onFinishSession,
  onPairWatch,
  pairingCode = null,
  hoverZone = null,
  celebrationData = null,
  bestOfSets = 3,
  // MVP rotation props
  nextGame = null,
  sitsOutNames = '',
  sessionPlayers = [],
  currentSessionStats = {},
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeTab, setActiveTab] = useState('onCourt'); // 'onCourt' | 'standings'
  const [isWatchModalOpen, setIsWatchModalOpen] = useState(false);

  // Auto-detect orientation (Portrait vs Landscape) & Viewport Height
  const [isPortrait, setIsPortrait] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight > window.innerWidth;
    }
    return false;
  });

  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight;
    }
    return 800;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Helper to format player names & avatars
  const formatTeam = (players) => {
    return (players || []).map((p) => {
      const name = typeof p === 'string' ? p : p?.name || 'Player';
      const avatar = getPlayerAvatar(allPlayers, name);
      return { name, avatar };
    });
  };

  // Determine if Team 1 or Team 2 is serving so the serving team is ALWAYS on Left (Landscape) / Top (Portrait)
  const isTeam1Serving = (() => {
    if (!servingPlayer) return true;
    const sName = (typeof servingPlayer === 'string' ? servingPlayer : servingPlayer?.name || '').trim().toLowerCase();
    const t1Names = (team1Players || []).map((p) => (typeof p === 'string' ? p : p?.name || '').trim().toLowerCase());
    return t1Names.includes(sName);
  })();

  const leftTeamPlayers = isTeam1Serving ? team1Players : team2Players;
  const rightTeamPlayers = isTeam1Serving ? team2Players : team1Players;

  const leftTeamIndex = isTeam1Serving ? 1 : 2;
  const rightTeamIndex = isTeam1Serving ? 2 : 1;

  const leftTeam = formatTeam(leftTeamPlayers);
  const rightTeam = formatTeam(rightTeamPlayers);

  const leftScore = isTeam1Serving ? team1ScoreDisplay : team2ScoreDisplay;
  const rightScore = isTeam1Serving ? team2ScoreDisplay : team1ScoreDisplay;

  // Identifies the current game so a score reset (new game / rotation) animates differently from a point
  const gameKey = [
    sets[0], sets[1], games[0], games[1],
    leftTeam.map((p) => p.name).join('|'),
    rightTeam.map((p) => p.name).join('|'),
  ].join('/');

  // Brief glow on the half that just won a point
  const [pointPulse, setPointPulse] = useState(null);
  const lastScoresRef = useRef({ leftScore, rightScore, gameKey });
  useEffect(() => {
    const last = lastScoresRef.current;
    lastScoresRef.current = { leftScore, rightScore, gameKey };
    if (last.gameKey !== gameKey) return;
    const pointRank = (v) => ({ '0': 0, '15': 1, '30': 2, '40': 3, AD: 4 }[String(v).toUpperCase()] ?? (parseFloat(v) || 0));
    const leftUp = pointRank(leftScore) > pointRank(last.leftScore);
    const rightUp = pointRank(rightScore) > pointRank(last.rightScore);
    let side = leftUp ? 'left' : rightUp ? 'right' : null;
    // Back to deuce: the side that lost advantage drops, so the point went to the other side
    if (!side && String(leftScore) === '40' && String(rightScore) === '40') {
      if (String(last.leftScore).toUpperCase() === 'AD') side = 'right';
      if (String(last.rightScore).toUpperCase() === 'AD') side = 'left';
    }
    if (side) setPointPulse((p) => ({ side, id: (p?.id || 0) + 1 }));
  }, [leftScore, rightScore, gameKey]);
  const renderPulse = (side) =>
    pointPulse?.side === side ? <span key={pointPulse.id} className="score-pulse" aria-hidden="true" /> : null;

  const leftDwellZone = isTeam1Serving ? 'TEAM_A_HALF' : 'TEAM_B_HALF';
  const rightDwellZone = isTeam1Serving ? 'TEAM_B_HALF' : 'TEAM_A_HALF';

  const isHoverLeft = hoverZone === leftDwellZone;
  const isHoverRight = hoverZone === rightDwellZone;
  const isUndoHover = hoverZone === 'UNDO';
  const isWatchHover = hoverZone === 'PAIR_WATCH';
  const isSettingsHover = hoverZone === 'SETTINGS';
  const isResetHover = hoverZone === 'RESET' || hoverZone === 'RESET_MATCH' || hoverZone === 'FINISH_SESSION';
  const isStatsHover = hoverZone === 'STATS' || hoverZone === 'STANDINGS';

  const isCelebrationActive = !!celebrationData;
  const isCompactHeight = viewportHeight < 520;
  const isVeryShortHeight = viewportHeight < 360;

  // Format rotation server & sitting out info text
  const formattedSitsOut = sitsOutNames
    ? (typeof sitsOutNames === 'string' ? sitsOutNames : sitsOutNames.name || '')
    : '';
  const serverFirstName = servingPlayer ? servingPlayer.trim().split(' ')[0] : '';
  const sitsOutFirstName = formattedSitsOut ? formattedSitsOut.trim().split(' ')[0] : '';

  let rotationInfoText = '';
  if (serverFirstName && sitsOutFirstName) {
    rotationInfoText = `${serverFirstName} serving, ${sitsOutFirstName} sits out`;
  } else if (serverFirstName) {
    rotationInfoText = `${serverFirstName} serving`;
  } else if (sitsOutFirstName) {
    rotationInfoText = `${sitsOutFirstName} sits out`;
  }

  // Process set matrix - left/top = server's games, right/bottom = receiver's games
  const maxSets = Math.min(bestOfSets || 3, 3);
  const currentSetIndex = Math.min(maxSets - 1, (sets[0] || 0) + (sets[1] || 0));
  const setRows = Array.from({ length: maxSets }, (_, idx) => idx).map((idx) => {
    const scoreObj = setScores[idx];
    let t1G = '-';
    let t2G = '-';
    let t1W = false;
    let t2W = false;
    let isActive = false;

    if (idx < currentSetIndex && scoreObj) {
      t1G = Array.isArray(scoreObj) ? scoreObj[0] : scoreObj.team1 ?? 0;
      t2G = Array.isArray(scoreObj) ? scoreObj[1] : scoreObj.team2 ?? 0;
      t1W = t1G > t2G;
      t2W = t2G > t1G;
    } else if (idx === currentSetIndex) {
      t1G = games[0];
      t2G = games[1];
      isActive = true;
    }

    const leftGames = isTeam1Serving ? t1G : t2G;
    const rightGames = isTeam1Serving ? t2G : t1G;
    const leftWon = isTeam1Serving ? t1W : t2W;
    const rightWon = isTeam1Serving ? t2W : t1W;

    return { leftGames, rightGames, leftWon, rightWon, isActive };
  });

  const handleAvatarClick = (e, key) => {
    e.stopPropagation();
    setActiveTooltip(activeTooltip === key ? null : key);
  };

  // Helper component for team avatars (8px gap without overlap, solid 4px border: gold for server, white for others)
  const renderAvatars = (team, side, isVertical = false) => {
    return (
      <div className={`flex ${isVertical ? 'flex-col gap-2' : 'flex-row gap-2'} items-center justify-center relative flex-shrink-0 z-20`}>
        {team.map((player, idx) => {
          const isServing = servingPlayer === player.name;
          const tooltipKey = `${side}P${idx}`;
          return (
            <div key={idx} className="relative flex items-center justify-center">
              {/* DYNAMIC TOOLTIP ANCHORED RIGHT ABOVE AVATAR POINTING DOWNWARDS */}
              {activeTooltip === tooltipKey && (
                <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-[#e2e8f0] text-slate-950 font-extrabold text-xs px-3.5 py-1.5 rounded-full shadow-2xl z-50 whitespace-nowrap animate-fade-in flex flex-col items-center pointer-events-none">
                  <span>{player.name}</span>
                  {/* DOWNWARD ARROW NOTCH */}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#e2e8f0] rotate-45 shadow-sm" />
                </div>
              )}

              {/* AVATAR CONTAINER WITH SOLID 4PX BORDER: GOLD FOR SERVER, WHITE FOR OTHERS (NO SHADOW/GLOW) */}
              <div className={`relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 aspect-square rounded-full flex-shrink-0 overflow-hidden border-4 transition-all ${
                isServing ? 'border-[#f5b83d]' : 'border-white'
              }`}>
                <img
                  src={player.avatar}
                  alt={player.name}
                  onClick={(e) => handleAvatarClick(e, tooltipKey)}
                  className="w-full h-full aspect-square object-cover rounded-full cursor-pointer hover:scale-105 transition-transform"
                />
              </div>

              {/* SERVING BADGE WITH GOOGLE MATERIAL SYMBOL 'padel' ICON */}
              {isServing && (
                <div className="absolute bottom-0 right-0 w-5.5 h-5.5 sm:w-7 sm:h-7 lg:w-8.5 lg:h-8.5 rounded-full bg-[#f5b83d] border-2 border-[#0b152e] flex items-center justify-center shadow-lg z-30 translate-x-1 translate-y-1">
                  <span className="material-symbols-outlined text-slate-950 font-black text-xs sm:text-base lg:text-lg select-none leading-none">
                    padel
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative w-full h-full bg-transparent text-slate-100 flex flex-col justify-between p-1.5 sm:p-4 lg:p-6 overflow-hidden select-none">
      
      {/* TOP HEADER STATUS BAR */}
      <header className="relative z-20 flex items-center justify-center w-full max-w-6xl mx-auto pt-0.5 h-6">
        {mode === 'mvp' && rotationInfoText && !isPortrait && activeTab !== 'standings' && (
          /* Landscape mode rotation info badge centered at top line */
          <div className="rotation-chip left-1/2 -translate-x-1/2 bg-[#141f3d]/90 border border-slate-700/80 text-slate-100 font-extrabold text-xs sm:text-sm px-4 py-1.5 rounded-full shadow-lg backdrop-blur-md whitespace-nowrap z-30 flex items-center gap-1.5">
            <span>{rotationInfoText}</span>
          </div>
        )}
      </header>

      {/* MVP STANDINGS MODAL OVERLAY */}
      {mode === 'mvp' && activeTab === 'standings' && (
        <div
          onClick={() => setActiveTab('onCourt')}
          className="modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal modal-wide flex flex-col"
          >
            <ModalCloseButton onClick={() => setActiveTab('onCourt')} label="Close standings" />

            {/* Header Title */}
            <h2 className="modal-title">
              Session Standings
            </h2>

            {/* Standings Table */}
            <div className="bg-slate-900/90 border border-slate-700/80 rounded-2xl overflow-hidden shadow-2xl flex-1 overflow-y-auto">
              {/* TABLE HEADER */}
              <div className="grid grid-cols-12 items-center px-4 py-3 bg-[#1a264a] border-b border-slate-700/80 text-xs font-black text-slate-300 uppercase tracking-wider sticky top-0 z-10">
                <span className="col-span-6">PLAYER</span>
                <span className="col-span-2 text-center">W</span>
                <span className="col-span-2 text-center">L</span>
                <span className="col-span-2 text-right">%</span>
              </div>

              {/* TABLE BODY */}
              <div className="divide-y divide-slate-800/80">
                {[...sessionPlayers]
                  .map((playerObj) => {
                    const name = typeof playerObj === 'string' ? playerObj : playerObj?.name;
                    const stats = currentSessionStats[name] || { wins: 0, losses: 0, gamesPlayed: 0 };
                    const wins = stats.wins || 0;
                    const losses = stats.losses || 0;
                    const totalGames = wins + losses;
                    const winRate = totalGames > 0 ? (wins / totalGames) * 100 : 0;
                    return {
                      name,
                      wins,
                      losses,
                      totalGames,
                      winRate,
                      avatar: getPlayerAvatar(allPlayers, name),
                    };
                  })
                  .sort((a, b) => b.winRate - a.winRate || b.wins - a.wins || a.name.localeCompare(b.name))
                  .map((p, idx) => (
                    <div key={idx} className="grid grid-cols-12 items-center px-4 py-3.5 hover:bg-slate-800/40 transition-colors">
                      {/* PLAYER */}
                      <div className="col-span-6 flex items-center gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 aspect-square rounded-full overflow-hidden flex-shrink-0 border border-slate-600 shadow-md">
                          <img src={p.avatar} alt={p.name} className="w-full h-full object-cover rounded-full" />
                        </div>
                        <span className="font-extrabold text-slate-100 text-sm sm:text-base truncate">{p.name}</span>
                      </div>

                      {/* WINS */}
                      <span className="col-span-2 text-center font-extrabold text-slate-100 text-sm sm:text-base">{p.wins}</span>

                      {/* LOSSES */}
                      <span className="col-span-2 text-center font-extrabold text-slate-100 text-sm sm:text-base">{p.losses}</span>

                      {/* WIN % */}
                      <span className="col-span-2 text-right font-extrabold text-slate-100 text-sm sm:text-base">{Math.round(p.winRate)}%</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      {isPortrait ? (
        /* PORTRAIT LAYOUT */
        <main className={`relative flex-1 w-full max-w-md mx-auto flex flex-col justify-between my-auto z-10 transition-all duration-300 ${
          isCelebrationActive ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
        }`}>



          {/* ROTATION INFO BADGE OVERLAPPING PORTRAIT DIVIDER LINE */}
          {mode === 'mvp' && rotationInfoText && activeTab !== 'standings' && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#141f3d]/90 border border-slate-700/80 text-slate-100 font-extrabold text-xs sm:text-sm px-4 py-1.5 rounded-full shadow-lg backdrop-blur-md whitespace-nowrap z-30 flex items-center gap-1.5">
              <span>{rotationInfoText}</span>
            </div>
          )}

          {/* TOP HALF: SERVER TEAM */}
          <div
            data-dwell-zone={leftDwellZone}
            onClick={() => onPointScore(leftTeamIndex)}
            className={`relative flex-1 flex flex-col items-center justify-between pt-8 pb-0 cursor-pointer rounded-3xl transition-all duration-200 ${
              isHoverLeft ? 'bg-white/5' : ''
            }`}
          >
            {renderPulse('left')}
            {/* Team 1 Avatars (Top Centered, 8px Gap) */}
            <div className="pt-1">
              {renderAvatars(leftTeam, 'left', false)}
            </div>

            {/* Team 1 Score Box (Fixed Bounding Box Centered) */}
            <div className="flex-1 flex items-center justify-center my-auto">
              <div 
                className="flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{
                  width: 'min(224px, 52vw)',
                  height: 'min(224px, 24vh)',
                }}
              >
                <AnimatedScore
                  value={leftScore}
                  team={leftTeamIndex}
                  resetKey={gameKey}
                  className="font-nunito-black-italic text-white text-center leading-none select-none"
                  style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(80px, 22vw, 186px)' }}
                />
              </div>
            </div>

            {/* Team 1 Set Score Row (Classic Mode Only) */}
            {mode !== 'mvp' && (
              <div className="pb-2 z-20">
                <div className="flex items-center justify-center gap-3">
                  {setRows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`w-[48px] h-[58px] sm:w-[60px] sm:h-[73px] flex-shrink-0 flex items-center justify-center font-bold text-lg sm:text-2xl transition-all rounded-xl shadow-md ${
                        row.leftWon
                          ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                          : row.isActive
                          ? 'bg-[#a6c5ff] text-slate-950 font-black'
                          : 'bg-[#2a365c] text-slate-400 font-semibold'
                      }`}
                    >
                      <AnimatedScore value={row.leftGames} resetKey={idx} className="pill-roll" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* BOTTOM HALF: RECEIVER TEAM */}
          <div
            data-dwell-zone={rightDwellZone}
            onClick={() => onPointScore(rightTeamIndex)}
            className={`relative flex-1 flex flex-col items-center justify-between pb-8 pt-0 cursor-pointer rounded-3xl transition-all duration-200 ${
              isHoverRight ? 'bg-white/5' : ''
            }`}
          >
            {renderPulse('right')}
            {/* Team 2 Set Score Row (Classic Mode Only) */}
            {mode !== 'mvp' && (
              <div className="pt-2 z-20">
                <div className="flex items-center justify-center gap-3">
                  {setRows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`w-[48px] h-[58px] sm:w-[60px] sm:h-[73px] flex-shrink-0 flex items-center justify-center font-bold text-lg sm:text-2xl transition-all rounded-xl shadow-md ${
                        row.rightWon
                          ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                          : row.isActive
                          ? 'bg-[#a6c5ff] text-slate-950 font-black'
                          : 'bg-[#2a365c] text-slate-400 font-semibold'
                      }`}
                    >
                      <AnimatedScore value={row.rightGames} resetKey={idx} className="pill-roll" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team 2 Score Box (Fixed Bounding Box Centered) */}
            <div className="flex-1 flex items-center justify-center my-auto">
              <div 
                className="flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{
                  width: 'min(224px, 52vw)',
                  height: 'min(224px, 24vh)',
                }}
              >
                <AnimatedScore
                  value={rightScore}
                  team={rightTeamIndex}
                  resetKey={gameKey}
                  className="font-nunito-black-italic text-white text-center leading-none select-none"
                  style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(80px, 22vw, 186px)' }}
                />
              </div>
            </div>

            {/* Team 2 Avatars (Bottom Centered, 8px Gap) */}
            <div className="pb-1">
              {renderAvatars(rightTeam, 'right', false)}
            </div>
          </div>

        </main>
      ) : isCompactHeight ? (

        /* =========================================================
           COMPACT LANDSCAPE LAYOUT (< 520px height, e.g. phones in landscape)
           Exact 72px x 88px ratio set pills with 24px center gap.
           ========================================================= */
        <main className={`relative flex-1 w-full max-w-7xl mx-auto flex items-center justify-between my-auto z-10 transition-all duration-300 px-10 ${
          isCelebrationActive ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
        }`}>



          {/* Far Left: Team 1 Avatars (Vertically stacked, 8px gap) */}
          <div className="flex-shrink-0 z-20">
            {renderAvatars(leftTeam, 'left', true)}
          </div>

          {/* CENTER FLEX REGION */}
          <div className="flex-1 flex items-center justify-between min-w-0 h-full px-2 sm:px-4">
            
            {/* Left Space: Team 1 Score Box "40" floating in middle between Avatars and Center Set Block */}
            <div
              data-dwell-zone={leftDwellZone}
              onClick={() => onPointScore(leftTeamIndex)}
              className={`relative flex-1 flex items-center justify-center cursor-pointer py-1 px-2 rounded-3xl transition-all duration-200 ${
                isHoverLeft ? 'bg-white/5' : ''
              }`}
            >
              {renderPulse('left')}
              <div className="w-full max-w-[260px] h-[140px] sm:h-[180px] md:h-[220px] flex items-center justify-center flex-shrink-0 overflow-hidden">
                <AnimatedScore
                  value={leftScore}
                  team={leftTeamIndex}
                  resetKey={gameKey}
                  className="font-nunito-black-italic text-white text-center leading-none select-none"
                  style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(85px, 18vw, 190px)' }}
                />
              </div>
            </div>

            {/* Central Set Matrix Block (Evenly spaced 8px gap) */}
            {mode !== 'mvp' ? (
              <div className="flex items-center justify-center gap-2 sm:gap-2.5 z-20 flex-shrink-0 px-2 sm:px-4">
                {/* Team 1 Set Column */}
                <div className="flex flex-col gap-2">
                  {setRows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`w-[44px] h-[54px] sm:w-[50px] sm:h-[62px] flex-shrink-0 flex items-center justify-center font-bold text-base sm:text-xl transition-all rounded-xl shadow-md ${
                        row.leftWon
                          ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                          : row.isActive
                          ? 'bg-[#a6c5ff] text-slate-950 font-black'
                          : 'bg-[#2a365c] text-slate-400 font-semibold'
                      }`}
                    >
                      <AnimatedScore value={row.leftGames} resetKey={idx} className="pill-roll" />
                    </div>
                  ))}
                </div>

                {/* Team 2 Set Column */}
                <div className="flex flex-col gap-2">
                  {setRows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`w-[44px] h-[54px] sm:w-[50px] sm:h-[62px] flex-shrink-0 flex items-center justify-center font-bold text-base sm:text-xl transition-all rounded-xl shadow-md ${
                        row.rightWon
                          ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                          : row.isActive
                          ? 'bg-[#a6c5ff] text-slate-950 font-black'
                          : 'bg-[#2a365c] text-slate-400 font-semibold'
                      }`}
                    >
                      <AnimatedScore value={row.rightGames} resetKey={idx} className="pill-roll" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="w-12 sm:w-16 flex-shrink-0" />
            )}

            {/* Right Space: Team 2 Score Box "30" floating in middle between Center Set Block and Avatars */}
            <div
              data-dwell-zone={rightDwellZone}
              onClick={() => onPointScore(rightTeamIndex)}
              className={`relative flex-1 flex items-center justify-center cursor-pointer py-1 px-2 rounded-3xl transition-all duration-200 ${
                isHoverRight ? 'bg-white/5' : ''
              }`}
            >
              {renderPulse('right')}
              <div className="w-full max-w-[260px] h-[140px] sm:h-[180px] md:h-[220px] flex items-center justify-center flex-shrink-0 overflow-hidden">
                <AnimatedScore
                  value={rightScore}
                  team={rightTeamIndex}
                  resetKey={gameKey}
                  className="font-nunito-black-italic text-white text-center leading-none select-none"
                  style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(85px, 18vw, 190px)' }}
                />
              </div>
            </div>

          </div>

          {/* Far Right: Team 2 Avatars (Vertically stacked, 8px gap) */}
          <div className="flex-shrink-0 z-20">
            {renderAvatars(rightTeam, 'right', true)}
          </div>

        </main>
      ) : (

        /* =========================================================
           LARGE SCREEN LANDSCAPE LAYOUT (>= 520px height, e.g. tablets, 1280x800)
           ========================================================= */
        <main className={`relative flex-1 w-full max-w-7xl mx-auto flex items-center justify-between my-auto z-10 transition-all duration-300 px-10 lg:px-12 ${
          isCelebrationActive ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
        }`}>



          {/* LEFT HALF (SERVER TEAM) */}
          <div
            data-dwell-zone={leftDwellZone}
            onClick={() => onPointScore(leftTeamIndex)}
            className={`relative flex-1 h-full flex flex-col items-center justify-center cursor-pointer rounded-3xl transition-all duration-200 px-2 sm:px-4 ${
              isHoverLeft ? 'bg-white/5' : ''
            }`}
          >
            {renderPulse('left')}
            {/* Team 1 Avatars (Positioned Above Team 1 Score, 8px Gap) */}
            <div className="mb-2 sm:mb-4">
              {renderAvatars(leftTeam, 'left', false)}
            </div>

            {/* Team 1 Score Box */}
            <div className="w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] md:w-[280px] md:h-[280px] lg:w-[340px] lg:h-[340px] flex items-center justify-center flex-shrink-0 overflow-hidden">
              <AnimatedScore
                value={leftScore}
                team={leftTeamIndex}
                resetKey={gameKey}
                className="font-nunito-black-italic text-white text-center leading-none select-none"
                style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(100px, 20vw, 260px)' }}
              />
            </div>
          </div>

          {/* CENTER SET MATRIX (CLASSIC MODE ONLY) */}
          {mode !== 'mvp' && (
            <div className="flex items-center justify-center gap-6 z-20 flex-shrink-0 px-2 sm:px-6">
              {/* Team 1 Set Column */}
              <div className="flex flex-col gap-3 lg:gap-4">
                {setRows.map((row, idx) => (
                  <div
                    key={idx}
                    className={`w-[54px] h-[66px] md:w-[64px] md:h-[78px] lg:w-[72px] lg:h-[88px] flex-shrink-0 flex items-center justify-center font-bold text-xl md:text-2xl lg:text-3xl transition-all rounded-xl lg:rounded-2xl shadow-lg ${
                      row.leftWon
                        ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                        : row.isActive
                        ? 'bg-[#a6c5ff] text-slate-950 font-black'
                        : 'bg-[#2a365c] text-slate-400 font-semibold'
                    }`}
                  >
                    <AnimatedScore value={row.leftGames} resetKey={idx} className="pill-roll" />
                  </div>
                ))}
              </div>

              {/* Team 2 Set Column */}
              <div className="flex flex-col gap-3 lg:gap-4">
                {setRows.map((row, idx) => (
                  <div
                    key={idx}
                    className={`w-[54px] h-[66px] md:w-[64px] md:h-[78px] lg:w-[72px] lg:h-[88px] flex-shrink-0 flex items-center justify-center font-bold text-xl md:text-2xl lg:text-3xl transition-all rounded-xl lg:rounded-2xl shadow-lg ${
                      row.rightWon
                        ? 'bg-[#f5b83d] text-slate-950 font-black set-pill-won'
                        : row.isActive
                        ? 'bg-[#a6c5ff] text-slate-950 font-black'
                        : 'bg-[#2a365c] text-slate-400 font-semibold'
                    }`}
                  >
                    <AnimatedScore value={row.rightGames} resetKey={idx} className="pill-roll" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RIGHT HALF (RECEIVER TEAM) */}
          <div
            data-dwell-zone={rightDwellZone}
            onClick={() => onPointScore(rightTeamIndex)}
            className={`relative flex-1 h-full flex flex-col items-center justify-center cursor-pointer rounded-3xl transition-all duration-200 px-2 sm:px-4 ${
              isHoverRight ? 'bg-white/5' : ''
            }`}
          >
            {renderPulse('right')}
            {/* Team 2 Avatars (Positioned Above Team 2 Score, 8px Gap) */}
            <div className="mb-2 sm:mb-4">
              {renderAvatars(rightTeam, 'right', false)}
            </div>

            {/* Team 2 Score Box */}
            <div className="w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] md:w-[280px] md:h-[280px] lg:w-[340px] lg:h-[340px] flex items-center justify-center flex-shrink-0 overflow-hidden">
              <AnimatedScore
                value={rightScore}
                team={rightTeamIndex}
                resetKey={gameKey}
                className="font-nunito-black-italic text-white text-center leading-none select-none"
                style={{ fontFamily: "'Nunito Sans', sans-serif", fontStyle: 'italic', fontWeight: 900, fontSize: 'clamp(100px, 20vw, 260px)' }}
              />
            </div>
          </div>

        </main>
      )}

      {/* BOTTOM ACTION BAR (CIRCULAR BUTTONS WITH ACCESSIBILITY 48DP MINIMUM) */}
      <footer className={`relative z-30 flex items-center justify-center gap-3 sm:gap-5 md:gap-8 transition-all duration-300 ${
        isPortrait ? 'pt-1 pb-10 sm:pb-10 lg:pb-2' : isVeryShortHeight ? 'pt-0.5 pb-1' : 'pt-1 pb-2 sm:pb-5 lg:pb-6'
      } ${
        isCelebrationActive ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'
      }`}>
        {/* BUTTON 1: UNDO POINT */}
        <button
          data-dwell-zone="UNDO"
          onClick={onUndo}
          className={`min-w-[48px] min-h-[48px] ${
            isVeryShortHeight
              ? 'w-12 h-12'
              : 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
          } rounded-full bg-[#9ab3d5] text-[#080e21] flex items-center justify-center shadow-xl border border-white/20 transition-all duration-200 ${
            isUndoHover ? 'bg-[#f5b83d] text-slate-950 scale-110 ring-4 ring-[#f5b83d]/50 shadow-2xl' : 'hover:bg-[#b0c4e2]'
          }`}
          title="Undo Last Point"
        >
          <RotateCcw className={`${isVeryShortHeight ? 'w-5 h-5' : 'w-5 h-5 sm:w-6.5 sm:h-6.5 md:w-8 md:h-8'} stroke-[2.5]`} />
        </button>

        {/* BUTTON 2: PAIR SMARTWATCH COMPANION (native app only) */}
        {isNativeApp && (
          <button
            data-dwell-zone="PAIR_WATCH"
            onClick={() => setIsWatchModalOpen(true)}
            className={`min-w-[48px] min-h-[48px] ${
              isVeryShortHeight
                ? 'w-12 h-12'
                : 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
            } rounded-full bg-[#9ab3d5] text-[#080e21] flex items-center justify-center shadow-xl border border-white/20 transition-all duration-200 ${
              isWatchHover ? 'bg-[#f5b83d] text-slate-950 scale-110 ring-4 ring-[#f5b83d]/50 shadow-2xl' : 'hover:bg-[#b0c4e2]'
            }`}
            title="Pair Smartwatch"
          >
            <Watch className={`${isVeryShortHeight ? 'w-5 h-5' : 'w-5 h-5 sm:w-6.5 sm:h-6.5 md:w-8 md:h-8'} stroke-[2.5]`} />
          </button>
        )}

        {/* BUTTON 3: STATS / STANDINGS (MVP MODE ONLY) */}
        {mode === 'mvp' && (
          <button
            data-dwell-zone="STANDINGS"
            onClick={() => setActiveTab(activeTab === 'onCourt' ? 'standings' : 'onCourt')}
            className={`min-w-[48px] min-h-[48px] ${
              isVeryShortHeight
                ? 'w-12 h-12'
                : 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
            } rounded-full ${
              activeTab === 'standings' ? 'bg-[#f5b83d] text-slate-950' : 'bg-[#9ab3d5] text-[#080e21]'
            } flex items-center justify-center shadow-xl border border-white/20 transition-all duration-200 ${
              isStatsHover ? 'bg-[#f5b83d] text-slate-950 scale-110 ring-4 ring-[#f5b83d]/50 shadow-2xl' : 'hover:bg-[#b0c4e2]'
            }`}
            title="Session Standings & Stats"
          >
            <BarChart2 className={`${isVeryShortHeight ? 'w-5 h-5' : 'w-5 h-5 sm:w-6.5 sm:h-6.5 md:w-8 md:h-8'} stroke-[2.5]`} />
          </button>
        )}

        {/* BUTTON 4: MATCH SETTINGS */}
        <button
          data-dwell-zone="SETTINGS"
          onClick={onOpenSettings}
          className={`min-w-[48px] min-h-[48px] ${
            isVeryShortHeight
              ? 'w-12 h-12'
              : 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
          } rounded-full bg-[#9ab3d5] text-[#080e21] flex items-center justify-center shadow-xl border border-white/20 transition-all duration-200 ${
            isSettingsHover ? 'bg-[#f5b83d] text-slate-950 scale-110 ring-4 ring-[#f5b83d]/50 shadow-2xl' : 'hover:bg-[#b0c4e2]'
          }`}
          title="Match Settings"
        >
          <Settings className={`${isVeryShortHeight ? 'w-5 h-5' : 'w-5 h-5 sm:w-6.5 sm:h-6.5 md:w-8 md:h-8'} stroke-[2.5]`} />
        </button>

        {/* BUTTON 5: EXIT / FINISH SESSION */}
        <button
          data-dwell-zone="FINISH_SESSION"
          onClick={onFinishSession}
          className={`min-w-[48px] min-h-[48px] ${
            isVeryShortHeight
              ? 'w-12 h-12'
              : 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
          } rounded-full bg-[#9ab3d5] text-[#080e21] flex items-center justify-center shadow-xl border border-white/20 transition-all duration-200 ${
            isResetHover ? 'bg-rose-500 text-white scale-110 ring-4 ring-rose-500/50 shadow-2xl' : 'hover:bg-[#b0c4e2]'
          }`}
          title="Finish Session"
        >
          <LogOut className={`${isVeryShortHeight ? 'w-5 h-5' : 'w-5 h-5 sm:w-6.5 sm:h-6.5 md:w-8 md:h-8'} stroke-[2.5]`} />
        </button>
      </footer>

      {/* PAIR WATCH MODAL */}
      <PairWatchModal
        isOpen={isWatchModalOpen}
        pairingCode={pairingCode}
        onClose={() => setIsWatchModalOpen(false)}
      />
    </div>
  );
}
